import React, { useState, useContext, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Slide,
  Alert,
} from "@mui/material";
import { motion } from "framer-motion";
import { ReportContext } from "../../ReportContext";
import { AiOutlineFileAdd } from "react-icons/ai";

import SimpleTiptapEditor from "../../../components/tiptap-templates/simple/custom-editor";

// Slide transition for the dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AddProposal = ({ solutionId, reload, setReload }) => {
  const { addProposals } = useContext(ReportContext);
  const [open, setOpen] = useState(false);
  const [proposal, setProposal] = useState("");
  const sessionKey = `proposal_${solutionId}`;
  const [submitting, setSubmitting] = useState(false);
  const [editorHeight, setEditorHeight] = useState("400px");

  // Load saved proposal (if any)
  useEffect(() => {
    const saved = sessionStorage.getItem(sessionKey);
    if (saved) setProposal(saved);
  }, [sessionKey]);

  // Save proposal live as user types
  useEffect(() => {
    sessionStorage.setItem(sessionKey, proposal);
  }, [proposal, sessionKey]);

  // Adjust editor height based on dialog size
  useEffect(() => {
    if (open) {
      // Small delay to ensure dialog is rendered
      setTimeout(() => {
        const dialogContent = document.querySelector('.MuiDialogContent-root');
        if (dialogContent) {
          const availableHeight = dialogContent.clientHeight - 80; // Subtract padding
          setEditorHeight(`${Math.max(300, availableHeight)}px`);
        }
      }, 100);
    }
  }, [open]);

  const handleOpen = () => setOpen(true);
  
  const handleClose = () => {
    setOpen(false);
    // Clear any unsaved changes warning if needed
  };

  const handleSubmit = async () => {
    if (!proposal.trim()) return;
    // return console.log(proposal);
    

    setSubmitting(true);
    const response = await addProposals({
      solution_id: solutionId,
      notes: proposal, // This will be HTML content from the editor
    });

    if (response?.status === "success") {
      sessionStorage.removeItem(sessionKey);
      setProposal("");
      setReload(!reload);
      handleClose();
    }
    setSubmitting(false);
  };

  const handleEditorChange = (htmlContent) => {
    setProposal(htmlContent);
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpen}
        startIcon={<AiOutlineFileAdd />}
        sx={{
          textTransform: "none",
          borderRadius: "10px",
          fontWeight: 600,
          px: 2.5,
          py: 1,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          },
        }}
      >
        Add Proposal
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: 3,
            zIndex: 1400,
            overflow: "hidden",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
          component: motion.div,
          initial: { opacity: 0, scale: 0.95 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.95 },
          transition: { duration: 0.3, ease: "easeOut" },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            color: "primary.main",
            borderBottom: "1px solid #e0e0e0",
            fontSize: "1.35rem",
            py: 2,
            px: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            "& .MuiTypography-root": {
              color: "white",
            },
          }}
        >
          Submit Proposal
        </DialogTitle>

        <DialogContent 
          sx={{ 
            mt: 2, 
            px: 3,
            py: 2,
            overflowY: "auto",
            flex: 1,
          }}
        >
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              color: "#333",
              fontSize: "1rem",
            }}
          >
            Your Proposal
          </Typography>
          
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              overflow: "hidden",
              background: "#fff",
              transition: "all 0.3s ease",
              "&:hover": {
                // borderColor: "#10b981",
                boxShadow: "0 0 0 2px rgba(16, 185, 129, 0.1)",
              },
            }}
          >
            <SimpleTiptapEditor
              setContent={handleEditorChange}
              initialContent={proposal || "<p>Type your proposal here...</p>"}
              navbarHeight={60}
              debounceDelay={500}
            />
          </Box>
          
          <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <span>💾</span> Your progress is saved automatically
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <span>🖼️</span> You can drag & drop images
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid #e0e0e0",
            justifyContent: "space-between",
            background: "#fafafa",
          }}
        >
          <Button
            onClick={handleClose}
            color="inherit"
            sx={{ 
              textTransform: "none", 
              fontWeight: 500,
              "&:hover": {
                background: "#f5f5f5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!proposal.trim() || proposal === "<p></p>" || proposal === "<p>Type your proposal here...</p>" || submitting}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 4,
              py: 1,
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
              },
              "&.Mui-disabled": {
                background: "#d1d5db",
              },
            }}
          >
            {submitting ? "Submitting..." : "Submit Proposal"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddProposal;